import { useId } from 'react';
import { useTranslation } from 'react-i18next';

import Checkbox from '@/components/atoms/Checkbox';
import {
    orderLaksvelParameters,
    SMOLT_LAKSVEL_PARAMETERS,
    type SmoltLaksvelParameter,
} from '@/domain/smoltReport';

import { laksvelParameterLabel } from '../utils/laksvelParameterLabel';

import styles from './LaksvelParameterChecklist.module.scss';

interface LaksvelParameterChecklistProps {
    selected: readonly SmoltLaksvelParameter[];
    onSelectedChange: (selected: SmoltLaksvelParameter[]) => void;
}

/**
 * The Laksvel parameters a new smolt report includes; any of them, or none, can be checked. A
 * "Select all" box above them selects or clears the whole list.
 */
export default function LaksvelParameterChecklist(props: LaksvelParameterChecklistProps) {
    const { selected, onSelectedChange } = props;
    const { t } = useTranslation(['smoltReport', 'common']);
    // React's ids hold colons, which break the `#id` selectors jsdom and some libraries build.
    const id = useId().replaceAll(':', '');

    // Derived from the selection, so it cannot drift from the boxes below it.
    const selectedCount = SMOLT_LAKSVEL_PARAMETERS.filter((parameter) =>
        selected.includes(parameter)
    ).length;
    const allSelectedState =
        selectedCount === SMOLT_LAKSVEL_PARAMETERS.length
            ? true
            : selectedCount === 0
              ? false
              : 'indeterminate';

    // A partly selected group selects everything on click, as a native tri-state box does.
    function toggleAll(checked: boolean): void {
        onSelectedChange(checked ? [...SMOLT_LAKSVEL_PARAMETERS] : []);
    }

    function toggle(parameter: SmoltLaksvelParameter, checked: boolean): void {
        onSelectedChange(
            checked
                ? orderLaksvelParameters([...selected, parameter])
                : selected.filter((other) => other !== parameter)
        );
    }

    return (
        <fieldset className={styles.checklist}>
            <legend className={styles.legend}>
                {t('smoltReport:createSmoltReport.laksvelParametersLegend')}
            </legend>
            <div className={styles.selectAll}>
                <Checkbox
                    id={`${id}-all`}
                    label={t('smoltReport:createSmoltReport.selectAllLaksvelParameters')}
                    checked={allSelectedState}
                    onCheckedChange={toggleAll}
                />
            </div>
            <div className={styles.options}>
                {SMOLT_LAKSVEL_PARAMETERS.map((parameter) => (
                    <Checkbox
                        key={parameter}
                        id={`${id}-${parameter}`}
                        name="laksvelParameters"
                        value={parameter}
                        label={laksvelParameterLabel(t, parameter)}
                        checked={selected.includes(parameter)}
                        onCheckedChange={(checked) => toggle(parameter, checked)}
                    />
                ))}
            </div>
        </fieldset>
    );
}
